
import { motion } from 'framer-motion';
import { useInView } from '../hooks/useInView';

const About = () => {
  const [ref, isInView] = useInView({ threshold: 0.1 });

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const stats = [
    { number: '15+', label: 'Années d\'expérience' },
    { number: '2500+', label: 'Clients satisfaits' },
    { number: '24h', label: 'Intervention rapide' },
    { number: '100%', label: 'Garantie qualité' }
  ];

  return (
    <section id="about" ref={ref} className="py-16 md:py-20 lg:py-24 bg-gradient-to-b from-gray-50 to-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
          {/* Image Side */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="relative h-[500px] md:h-[600px] w-full rounded-lg overflow-hidden shadow-2xl">
              <img
                src="https://public.readdy.ai/ai/img_res/793d414a03c19632f9648c27e634b804.jpg"
                alt="Artisan vitrier FenetrePro Toulouse - Installation fenêtre double vitrage"
                title="Expert vitrier certifié - Réparation et installation fenêtre Toulouse"
                width="800"
                height="1000"
                loading="lazy"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute top-6 md:top-8 right-6 md:right-8 bg-white rounded-lg p-4 md:p-6 shadow-xl">
                <div className="text-center">
                  <div className="text-4xl md:text-5xl font-bold text-teal-600 mb-2">15+</div>
                  <div className="text-sm md:text-base text-gray-700 font-medium">Ans d\'expérience</div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Content Side */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-block bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
              <i className="ri-award-line mr-2"></i>
              Expert Certifié
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 break-words">
              Votre <strong className="text-teal-600">Expert</strong> en Réparation de Fenêtres
            </h2>
            <p className="text-base md:text-lg text-gray-600 mb-4 md:mb-6 leading-relaxed break-words">
              Depuis plus de 15 ans, <strong>FenetrePro Toulouse</strong> accompagne les particuliers et professionnels dans la <a href="#services" onClick={(e) => { e.preventDefault(); scrollToSection('services'); }} className="text-teal-600 hover:text-teal-700 font-semibold cursor-pointer underline decoration-teal-600/30 hover:decoration-teal-600 transition-all">réparation et l'entretien de leurs fenêtres</a>, <a href="#services" onClick={(e) => { e.preventDefault(); scrollToSection('services'); }} className="text-teal-600 hover:text-teal-700 font-semibold cursor-pointer underline decoration-teal-600/30 hover:decoration-teal-600 transition-all">baies vitrées</a> et <a href="#services" onClick={(e) => { e.preventDefault(); scrollToSection('services'); }} className="text-teal-600 hover:text-teal-700 font-semibold cursor-pointer underline decoration-teal-600/30 hover:decoration-teal-600 transition-all">volets roulants</a>. Notre expertise reconnue et notre engagement qualité font de nous le partenaire idéal pour tous vos projets de réparation de fenêtres à <strong>Toulouse</strong> et dans toute la région.
            </p>
            <p className="text-base md:text-lg text-gray-600 mb-6 md:mb-8 leading-relaxed break-words">
              Nos <a href="#team" onClick={(e) => { e.preventDefault(); scrollToSection('team'); }} className="text-teal-600 hover:text-teal-700 font-semibold cursor-pointer underline decoration-teal-600/30 hover:decoration-teal-600 transition-all">artisans qualifiés</a> interviennent rapidement pour diagnostiquer et réparer tous types de problèmes : vitres cassées, mécanismes défectueux, problèmes d'isolation, volets roulants bloqués. Nous utilisons uniquement des matériaux de qualité professionnelle et garantissons toutes nos interventions pendant 2 ans.
            </p>

            {/* Features */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 mb-6 md:mb-8">
              <div className="flex items-start">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-teal-100 rounded-lg flex items-center justify-center mr-3 md:mr-4 flex-shrink-0">
                  <i className="ri-shield-check-line text-xl md:text-2xl text-teal-600"></i>
                </div>
                <div className="break-words">
                  <h4 className="font-semibold text-sm md:text-base text-gray-900 mb-1">Garantie 2 ans</h4>
                  <p className="text-xs md:text-sm text-gray-600">Sur toutes nos prestations</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-orange-100 rounded-lg flex items-center justify-center mr-3 md:mr-4 flex-shrink-0">
                  <i className="ri-time-line text-xl md:text-2xl text-orange-600"></i>
                </div>
                <div className="break-words">
                  <h4 className="font-semibold text-sm md:text-base text-gray-900 mb-1">
                    <a href="#contact" onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }} className="hover:text-teal-600 cursor-pointer underline decoration-teal-600/30 hover:decoration-teal-600 transition-all">Intervention 24h</a>
                  </h4>
                  <p className="text-xs md:text-sm text-gray-600">Service d\'urgence disponible</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-teal-100 rounded-lg flex items-center justify-center mr-3 md:mr-4 flex-shrink-0">
                  <i className="ri-user-star-line text-xl md:text-2xl text-teal-600"></i>
                </div>
                <div className="break-words">
                  <h4 className="font-semibold text-sm md:text-base text-gray-900 mb-1">
                    <a href="#team" onClick={(e) => { e.preventDefault(); scrollToSection('team'); }} className="hover:text-teal-600 cursor-pointer underline decoration-teal-600/30 hover:decoration-teal-600 transition-all">Artisans qualifiés</a>
                  </h4>
                  <p className="text-xs md:text-sm text-gray-600">Experts certifiés</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-orange-100 rounded-lg flex items-center justify-center mr-3 md:mr-4 flex-shrink-0">
                  <i className="ri-file-text-line text-xl md:text-2xl text-orange-600"></i>
                </div>
                <div className="break-words">
                  <h4 className="font-semibold text-sm md:text-base text-gray-900 mb-1">
                    <a href="#contact" onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }} className="hover:text-teal-600 cursor-pointer underline decoration-teal-600/30 hover:decoration-teal-600 transition-all">Devis gratuit</a>
                  </h4>
                  <p className="text-xs md:text-sm text-gray-600">Sans engagement</p>
                </div>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-6 pt-6 md:pt-8 border-t border-gray-200">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-2xl md:text-3xl font-bold text-teal-600 mb-1">{stat.number}</div>
                  <div className="text-xs md:text-sm text-gray-600 break-words">{stat.label}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;
